/**
 * Compile-time schema↔interface pin (bidirectional mutual assignability).
 *
 * Every `export const *Schema` in this package MUST be created through
 * `pinned<T>()(schema)` — the wrapper IS the pin (enforced by the
 * no-restricted-syntax fence in eslint.config.mjs). Drift on either side
 * (field added to one, optionality mismatch, constraint changing the
 * inferred type) fails `tsc --noEmit` at the export site with the
 * expected/actual types in the error.
 *
 * Replaces the legacy 6-line `_Assert*` / `_pin*` blocks (one canonical form
 * → trivial presence guard).
 *
 * Runtime: identity function — schema is returned as-is, no allocation or
 * behavioural change. The wrapper exists only for the type system.
 *
 * @see .tondocs/WEB_MOBILE_CONTRACT.md §8 G — why interface stays the SoT
 */

import { z } from "zod";

type Mutual<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;

export const pinned =
  <T>() =>
  <S extends z.ZodType>(
    schema: S,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- `_drift` is the rest param tsc demands when Mutual<...> is `false`; its presence in the signature is the entire pin mechanism.
    ..._drift: Mutual<z.infer<S>, T> extends true
      ? []
      : [{ "schema/interface drift": { interfaceSide: T; schemaSide: z.infer<S> } }]
  ): S =>
    schema;

// Permanent negative probe — never executed (the arrow is never called, so
// there is no runtime schema allocation). Exists only so tsc checks the
// negative path: if `pinned` ever stops catching drift, the directive
// becomes unused and tsc fails.
const _driftProbe = () =>
  // @ts-expect-error — {a: string} interface vs {a: number} schema must not pin
  pinned<{ a: string }>()(z.object({ a: z.number() }));
void _driftProbe;
